import type { Aircraft, AircraftCategory, LogbookClass } from '@/lib/db';

/**
 * The two vocabularies, and the one-way trip between them.
 *
 * `AIRCRAFT_CATEGORIES` is what the CAA register says an aircraft *is*, in the
 * register's own words, so that the category on a record and the category in
 * the register are the same thing rather than a translation that can drift.
 *
 * `LOGBOOK_CLASSES` is what a CAR 61.29 page prints as its hour columns. The
 * two differ in both directions and that is the point: the register has no
 * single-versus-multi-engine and no seaplane, the logbook has no balloon
 * category to be told apart from an aeroplane, and an aircraft's category plus
 * its engine count decides its column without either field being able to
 * contradict the other.
 */

/** Display order and labels for `AircraftCategory` — the register's own categories. */
export const AIRCRAFT_CATEGORIES: readonly { id: AircraftCategory; label: string }[] = [
  { id: 'aeroplane', label: 'Aeroplane' },
  { id: 'microlight', label: 'Microlight' },
  { id: 'helicopter', label: 'Helicopter' },
  { id: 'gyroplane', label: 'Gyroplane' },
  { id: 'glider', label: 'Glider' },
  { id: 'balloon', label: 'Balloon' },
];

export function categoryLabel(id: AircraftCategory): string {
  return AIRCRAFT_CATEGORIES.find((option) => option.id === id)?.label ?? id;
}

/** Display order and labels for `LogbookClass` — a CAA-1373 column reads roughly in this order. */
export const LOGBOOK_CLASSES: readonly { id: LogbookClass; label: string }[] = [
  { id: 'single-engine-land', label: 'Single-engine land' },
  { id: 'multi-engine-land', label: 'Multi-engine land' },
  { id: 'sea', label: 'Seaplane' },
  { id: 'helicopter', label: 'Helicopter' },
  { id: 'gyroplane', label: 'Gyroplane' },
  { id: 'glider', label: 'Glider' },
  { id: 'microlight', label: 'Microlight' },
  { id: 'balloon', label: 'Balloon' },
];

export function logbookClassLabel(id: LogbookClass): string {
  return LOGBOOK_CLASSES.find((option) => option.id === id)?.label ?? id;
}

/**
 * Which logbook column this aircraft's hours belong in.
 *
 * The only place the engine count turns into a column, and the reason the
 * register's categories can be adopted verbatim without the logbook losing the
 * split it is required to print. Two engines or more is multi-engine; anything
 * less is single, including the nought a glider is entered with.
 *
 * A seaplane never comes out of here. The register has no category for one, so
 * claiming it from a category would be inventing the fact — a float-equipped
 * aeroplane reads as an aeroplane, and the pilot moves the entry to the Sea
 * column themselves, which is the same edit they would make to any other
 * derived cell.
 */
export function logbookClassFor(
  aircraft: Pick<Aircraft, 'category' | 'engineCount'>,
): LogbookClass {
  switch (aircraft.category) {
    case 'aeroplane':
      return aircraft.engineCount >= 2 ? 'multi-engine-land' : 'single-engine-land';
    case 'microlight':
      return 'microlight';
    case 'helicopter':
      return 'helicopter';
    case 'gyroplane':
      return 'gyroplane';
    case 'glider':
      return 'glider';
    case 'balloon':
      return 'balloon';
  }
}
